/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row, UncontrolledTooltip } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getInstitutions } from 'app/entities/institution/institution.reducer';
import { getEntities as getGlucoUsers } from 'app/entities/gluco-user/gluco-user.reducer';
import { getEntities as getPackageSubscribers } from 'app/entities/package-subscriber/package-subscriber.reducer';
import { InsurancePolicyStatus } from 'app/shared/model/enumerations/insurance-policy-status.model';
import { InsuranceType } from 'app/shared/model/enumerations/insurance-type.model';
import { createEntity, getEntity, reset, updateEntity } from './insurance-policy.reducer';

export const InsurancePolicyUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const institutions = useAppSelector(state => state.institution.entities);
  const glucoUsers = useAppSelector(state => state.glucoUser.entities);
  const packageSubscribers = useAppSelector(state => state.packageSubscriber.entities);
  const insurancePolicyEntity = useAppSelector(state => state.insurancePolicy.entity);
  const loading = useAppSelector(state => state.insurancePolicy.loading);
  const updating = useAppSelector(state => state.insurancePolicy.updating);
  const updateSuccess = useAppSelector(state => state.insurancePolicy.updateSuccess);
  const insurancePolicyStatusValues = Object.keys(InsurancePolicyStatus);
  const insuranceTypeValues = Object.keys(InsuranceType);

  const handleClose = () => {
    navigate(`/insurance-policy${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getInstitutions({}));
    dispatch(getGlucoUsers({}));
    dispatch(getPackageSubscribers({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.startDate !== undefined && typeof values.startDate !== 'number') {
      values.startDate = Number(values.startDate);
    }
    if (values.expiryDate !== undefined && typeof values.expiryDate !== 'number') {
      values.expiryDate = Number(values.expiryDate);
    }
    if (values.allowedMoney !== undefined && typeof values.allowedMoney !== 'number') {
      values.allowedMoney = Number(values.allowedMoney);
    }
    if (values.usedMoney !== undefined && typeof values.usedMoney !== 'number') {
      values.usedMoney = Number(values.usedMoney);
    }
    values.microSec = convertDateTimeToServer(values.microSec);
    values.timestampEntry = convertDateTimeToServer(values.timestampEntry);
    values.lastUpdated = convertDateTimeToServer(values.lastUpdated);
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }

    const entity = {
      ...insurancePolicyEntity,
      ...values,
      provider: institutions.find(it => it.id.toString() === values.provider?.toString()),
      patient: glucoUsers.find(it => it.id.toString() === values.patient?.toString()),
      subscriber: packageSubscribers.find(it => it.id.toString() === values.subscriber?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          microSec: displayDefaultDateTime(),
          timestampEntry: displayDefaultDateTime(),
          lastUpdated: displayDefaultDateTime(),
        }
      : {
          status: 'IDLE',
          insuranceType: 'REGULAR',
          ...insurancePolicyEntity,
          microSec: convertDateTimeFromServer(insurancePolicyEntity.microSec),
          timestampEntry: convertDateTimeFromServer(insurancePolicyEntity.timestampEntry),
          lastUpdated: convertDateTimeFromServer(insurancePolicyEntity.lastUpdated),
          provider: insurancePolicyEntity?.provider?.id,
          patient: insurancePolicyEntity?.patient?.id,
          subscriber: insurancePolicyEntity?.subscriber?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.insurancePolicy.home.createOrEditLabel" data-cy="InsurancePolicyCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.insurancePolicy.home.createOrEditLabel">Create or edit a InsurancePolicy</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="insurance-policy-id"
                  label={translate('tinyMedicRestApp.insurancePolicy.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.startDate')}
                id="insurance-policy-startDate"
                name="startDate"
                data-cy="startDate"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.expiryDate')}
                id="insurance-policy-expiryDate"
                name="expiryDate"
                data-cy="expiryDate"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.policyNumber')}
                id="insurance-policy-policyNumber"
                name="policyNumber"
                data-cy="policyNumber"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.allowedMoney')}
                id="insurance-policy-allowedMoney"
                name="allowedMoney"
                data-cy="allowedMoney"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.usedMoney')}
                id="insurance-policy-usedMoney"
                name="usedMoney"
                data-cy="usedMoney"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.status')}
                id="insurance-policy-status"
                name="status"
                data-cy="status"
                type="select"
              >
                {insurancePolicyStatusValues.map(insurancePolicyStatus => (
                  <option value={insurancePolicyStatus} key={insurancePolicyStatus}>
                    {translate(`tinyMedicRestApp.InsurancePolicyStatus.${insurancePolicyStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.insuranceType')}
                id="insurance-policy-insuranceType"
                name="insuranceType"
                data-cy="insuranceType"
                type="select"
              >
                {insuranceTypeValues.map(insuranceType => (
                  <option value={insuranceType} key={insuranceType}>
                    {translate(`tinyMedicRestApp.InsuranceType.${insuranceType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.active')}
                id="insurance-policy-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.microSec')}
                id="insurance-policy-microSec"
                name="microSec"
                data-cy="microSec"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.timestampEntry')}
                id="insurance-policy-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.lastUpdated')}
                id="insurance-policy-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.cursor')}
                id="insurance-policy-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.employerName')}
                id="insurance-policy-employerName"
                name="employerName"
                data-cy="employerName"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insurancePolicy.lastUpdateReason')}
                id="insurance-policy-lastUpdateReason"
                name="lastUpdateReason"
                data-cy="lastUpdateReason"
                type="text"
              />
              <UncontrolledTooltip target="lastUpdateReasonLabel">
                <Translate contentKey="tinyMedicRestApp.insurancePolicy.help.lastUpdateReason" />
              </UncontrolledTooltip>
              <ValidatedField
                id="insurance-policy-provider"
                name="provider"
                data-cy="provider"
                label={translate('tinyMedicRestApp.insurancePolicy.provider')}
                type="select"
                required
              >
                <option value="" key="0" />
                {institutions
                  ? institutions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="insurance-policy-patient"
                name="patient"
                data-cy="patient"
                label={translate('tinyMedicRestApp.insurancePolicy.patient')}
                type="select"
                required
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="insurance-policy-subscriber"
                name="subscriber"
                data-cy="subscriber"
                label={translate('tinyMedicRestApp.insurancePolicy.subscriber')}
                type="select"
              >
                <option value="" key="0" />
                {packageSubscribers
                  ? packageSubscribers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/insurance-policy" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default InsurancePolicyUpdate;
